import React, { createContext, useCallback, useContext, useEffect, useState } from "react";
import { Button } from "@/components/ui/button";
import { Eye, EyeOff } from "lucide-react";

/**
 * Sistema GLOBAL de ocultação de valores financeiros.
 *
 * - Estado salvo em localStorage (`gestaoexpert:hide_money`).
 * - Padrão: visível.
 * - Ao alternar, todas as telas que consomem este contexto refletem
 *   instantaneamente (sem reload).
 *
 * Uso:
 *   const { hidden, toggle, mask, maskBrl } = useHiddenValues();
 *   <Money value={500} />            // R$ 500,00 ou R$ *****
 *   <Money value={500} format="num" /> // 500 ou *****
 *   <HideMoneyButton />              // botão de olho global
 */
const HiddenValuesContext = createContext(null);

const STORAGE_KEY = "gestaoexpert:hide_money";

export function HiddenValuesProvider({ children }) {
  const [hidden, setHidden] = useState(() => {
    try { return localStorage.getItem(STORAGE_KEY) === "1"; } catch { return false; }
  });

  // Sincroniza entre abas/janelas do mesmo dispositivo
  useEffect(() => {
    const onStorage = (e) => {
      if (e.key === STORAGE_KEY) setHidden(e.newValue === "1");
    };
    window.addEventListener("storage", onStorage);
    return () => window.removeEventListener("storage", onStorage);
  }, []);

  const toggle = useCallback(() => {
    setHidden(prev => {
      const next = !prev;
      try { localStorage.setItem(STORAGE_KEY, next ? "1" : "0"); } catch { /* ignore */ }
      return next;
    });
  }, []);

  // Helpers prontos pra uso. `mask` retorna o placeholder genérico,
  // `maskBrl` retorna "R$ *****" (mantém prefixo monetário).
  const mask = useCallback((value) => (hidden ? "*****" : value), [hidden]);
  const maskBrl = useCallback((rendered) => (hidden ? "R$ *****" : rendered), [hidden]);

  const value = { hidden, toggle, mask, maskBrl };
  return (
    <HiddenValuesContext.Provider value={value}>{children}</HiddenValuesContext.Provider>
  );
}

export function useHiddenValues() {
  const ctx = useContext(HiddenValuesContext);
  if (!ctx) throw new Error("useHiddenValues precisa estar dentro de HiddenValuesProvider");
  return ctx;
}

/**
 * Componente reutilizável para qualquer valor monetário.
 * `formatFn` opcional permite formatação custom (ex: `n => brl(n)`).
 * Se não fornecida, usa formatação BRL padrão.
 */
const defaultBrl = (n) => {
  const num = Number(n || 0);
  return new Intl.NumberFormat("pt-BR", { style: "currency", currency: "BRL" }).format(num);
};

export function Money({ value, formatFn = defaultBrl, mask: customMask = "R$ *****", className }) {
  const { hidden } = useHiddenValues();
  return <span className={className}>{hidden ? customMask : formatFn(value)}</span>;
}

/**
 * Botão GLOBAL de visibilidade.
 * Aparece como toggle de olho. Use no topo de páginas com valores financeiros.
 *
 * Props:
 *   variant: "outline" (default) | "ghost"
 *   size: "default" (default) | "sm" | "icon"
 *   label: texto opcional ao lado do ícone (default: nenhum)
 */
export function HideMoneyButton({ variant = "outline", size = "sm", label = "", className = "", testId = "hide-money-btn" }) {
  const { hidden, toggle } = useHiddenValues();
  return (
    <Button
      type="button"
      variant={variant}
      size={size}
      onClick={toggle}
      title={hidden ? "Mostrar valores" : "Ocultar valores"}
      aria-label={hidden ? "Mostrar valores" : "Ocultar valores"}
      data-testid={testId}
      className={className}
    >
      {hidden ? <EyeOff size={14} className={label ? "mr-1.5" : ""}/> : <Eye size={14} className={label ? "mr-1.5" : ""}/>}
      {label}
    </Button>
  );
}
